import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    UserGroupIcon,
    ChartBarIcon,
    CurrencyDollarIcon,
    GlobeAltIcon,
    AcademicCapIcon,
    ArrowPathIcon,
    ShoppingCartIcon,
    BuildingStorefrontIcon,
    CubeIcon,
    TruckIcon,
} from '@heroicons/react/24/outline';

import KPICard from '../components/KPICard';
import ActivityFeed from '../components/ActivityFeed';
import DashboardCharts from '../components/dashboard/DashboardCharts';
import MiniMap from '../components/dashboard/MiniMap';
import QuickActions from '../components/dashboard/QuickActions';
import UpcomingEvents from '../components/dashboard/UpcomingEvents';
import { CardSkeleton } from '../components/skeletons';
import AlertBanner from '../components/alerts/AlertBanner';

import { apiGet, apiPatch, subscribeToDataInvalidation } from '../utils/api';
import { API_URL } from '../api/config';
import { useCurrency } from '../hooks/useCurrency';
import { useAuth } from '../contexts/AuthContext';

interface DashboardKPIData {
    farmers: number;
    vslaSavings: number;
    compostProduced: number;
    totalRevenue: number;
    trainingSessions: number;
    timestamp?: string;
}

interface Alert {
    id: number;
    alert_type: string;
    severity: 'critical' | 'warning' | 'info' | 'success';
    title: string;
    message: string;
    action_url?: string;
    entity_name?: string;
    threshold_value?: number;
    actual_value?: number;
    created_at: string;
}

interface Order {
    id: string;
    orderNumber: string;
    buyerName: string;
    totalValue: number;
    status: string;
    paymentStatus: string;
    createdAt: string;
}

interface WarehouseStats {
    totalFacilities?: number;
    totalCapacity: string | number;
    totalStored: string | number;
    usagePercentage?: string | number;
    utilizationRate?: string | number;
    lossRate: string | number;
    operationalFacilities?: number;
    avgStorageDuration?: string | number;
    maintenanceFund?: string | number;
    revenue?: string | number;
    losses?: string | number;
}

interface MapStats {
    total_farmers: number;
    total_warehouses: number;
    total_aggregation_centers: number;
    total_area_hectares: number;
}

interface Warehouse {
    id: number;
    name: string;
    type: string;
    location_lat: number;
    location_lng: number;
    location_name: string;
    capacity_kg: number;
    current_usage_kg: number;
    temperature_celsius: number | null;
    status: string;
    usage_percentage: number;
}

interface FarmerPlot {
    id: number;
    name: string;
    household_type: string;
    plot_boundary: any[];
}

interface AggregationCenter {
    id: number;
    name: string;
    location_lat: number;
    location_lng: number;
    buyer_partners: string[];
    operating_hours: string;
    contact_info: string;
    status: string;
}

interface FundingRequest {
    id: number;
    category?: string;
    amount?: number;
    reason?: string;
    status?: string;
    created_at?: string;
    requested_by?: string;
}

const Dashboard = () => {
    const { t } = useTranslation();
    const { format: formatCurrency, formatCompact: formatCurrencyCompact } = useCurrency();
    const { user } = useAuth();
    const tenantName = user?.tenant?.name || user?.tenant?.company_name || 'Current workspace';
    const [kpiData, setKpiData] = useState<DashboardKPIData | null>(null);
    const [chartData, setChartData] = useState<any>(null);
    const [eventsData, setEventsData] = useState<any[]>([]);
    const [recentOrders, setRecentOrders] = useState<Order[]>([]);
    const [warehouseStats, setWarehouseStats] = useState<WarehouseStats | null>(null);
    const [mapStats, setMapStats] = useState<MapStats | null>(null);
    const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
    const [farmers, setFarmers] = useState<FarmerPlot[]>([]);
    const [aggregationCenters, setAggregationCenters] = useState<AggregationCenter[]>([]);

    const [alerts, setAlerts] = useState<Alert[]>([]);
    const [fundingRequests, setFundingRequests] = useState<FundingRequest[]>([]);
    const [fundingRequestError, setFundingRequestError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [timeFilter, setTimeFilter] = useState<'7d' | '30d' | 'ytd'>('30d');
    const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

    const labelize = (value: unknown, fallback = 'Not set') =>
        String(value ?? fallback).replace(/_/g, ' ');

    const toNumber = (value: unknown, fallback = 0) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
    };

    const featureEnabled = useCallback((key: string) => {
        if (user?.role === 'super_admin') return true;
        const feature = user?.tenant_features?.[key];
        return feature ? feature.enabled !== false && feature.is_enabled !== false : false;
    }, [user?.role, user?.tenant_features]);

    const pageEnabled = useCallback((key: string) => {
        if (user?.role === 'super_admin') return true;
        const page = user?.tenant_pages?.[key];
        return page ? page.enabled !== false && page.is_enabled !== false : false;
    }, [user?.role, user?.tenant_pages]);

    const dashboardAccess = React.useMemo(() => ({
        farmers: featureEnabled('farmers') && pageEnabled('farmers'),
        maps: featureEnabled('maps') && pageEnabled('maps'),
        warehouse: featureEnabled('warehouse') && pageEnabled('warehouse'),
        orders: featureEnabled('orders') && pageEnabled('orders'),
        vsla: featureEnabled('vsla') && pageEnabled('vsla'),
        compost: featureEnabled('compost') && pageEnabled('compost'),
        training: featureEnabled('training') && pageEnabled('training'),
        issues: featureEnabled('issues') && pageEnabled('issues'),
        traceability: featureEnabled('traceability') && pageEnabled('traceability'),
        activities: featureEnabled('activities') && pageEnabled('activities'),
        farmFinance: featureEnabled('farm_finance') && pageEnabled('farm-finance'),
        marketplace: featureEnabled('marketplace') || featureEnabled('shared_marketplace'),
    }), [featureEnabled, pageEnabled]);

    const canReviewFundingRequests = dashboardAccess.farmFinance &&
        ['owner', 'project_manager'].includes(user?.role || '');

    const shouldLoadCharts = React.useMemo(() => (
        dashboardAccess.farmers ||
        dashboardAccess.vsla ||
        dashboardAccess.marketplace ||
        dashboardAccess.compost ||
        dashboardAccess.training
    ), [dashboardAccess]);

    const shouldLoadEvents = React.useMemo(() => (
        dashboardAccess.activities || dashboardAccess.training
    ), [dashboardAccess.activities, dashboardAccess.training]);

    const enabledModuleNames = React.useMemo(() => [
        dashboardAccess.farmers && 'Farmers',
        dashboardAccess.maps && 'Farm Maps',
        dashboardAccess.warehouse && 'Warehouse',
        dashboardAccess.orders && 'Orders',
        dashboardAccess.vsla && 'VSLA',
        dashboardAccess.compost && 'Compost',
        dashboardAccess.training && 'Training',
    ].filter(Boolean) as string[], [dashboardAccess]);

    const staffOpsSummary = enabledModuleNames.length > 0
        ? `${tenantName} workspace dashboard for ${enabledModuleNames.join(', ')}.`
        : `${tenantName} workspace dashboard. Ask Super Admin to enable operational modules.`;

    useEffect(() => {
        document.title = 'eSanza Staff Dashboard | Operations Control Center';
    }, []);

    const refreshData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            await Promise.all([
                fetchDashboardData(),
                fetchAlerts()
            ]);
            setLastUpdated(new Date());
        } catch (error) {
            console.error("Refresh failed", error);
        } finally {
            setLoading(false);
        }
    }, [timeFilter, dashboardAccess, user?.role]);

    useEffect(() => {
        // Initial fetch + poll revalidation
        refreshData();
        const intervalId = setInterval(refreshData, 5 * 60 * 1000);
        return () => clearInterval(intervalId);
    }, [refreshData]);

    useEffect(() => {
        // Mutation-triggered invalidation refresh
        return subscribeToDataInvalidation(() => {
            refreshData();
        });
    }, [refreshData]);

    const fetchAlerts = async () => {
        try {
            const data = await apiGet<{ alerts: Alert[] }>('/api/alerts');
            setAlerts(data.alerts || []);
        } catch (err) {
            console.error('Failed to fetch alerts:', err);
        }
    };

    const fetchDashboardData = async () => {
        try {
            const token = localStorage.getItem('aaywa_token');
            const headers: HeadersInit = token ? { 'Authorization': `Bearer ${token}` } : {};
            const enabledFetch = (enabled: boolean, url: string) =>
                enabled ? fetch(url, { headers }) : Promise.resolve(null);

            // Execute all dashboard API calls in parallel
            const [
                response,
                chartsResponse,
                eventsResponse,
                ordersResponse,
                warehouseStatsResponse,
                mapStatsResponse,
                warehousesResponse,
                farmersResponse,
                aggregationCentersResponse,
                fundingRequestsResponse
            ] = await Promise.all([
                fetch(`${API_URL}/api/dashboard/kpi?range=${timeFilter}`, { headers }),
                enabledFetch(shouldLoadCharts, `${API_URL}/api/dashboard/charts`),
                enabledFetch(shouldLoadEvents, `${API_URL}/api/dashboard/events`),
                enabledFetch(dashboardAccess.orders, `${API_URL}/api/marketplace/orders`),
                enabledFetch(dashboardAccess.warehouse, `${API_URL}/api/warehouses/stats`),
                enabledFetch(dashboardAccess.maps, `${API_URL}/api/maps/stats`),
                enabledFetch(dashboardAccess.warehouse, `${API_URL}/api/warehouses/facilities`),
                enabledFetch(dashboardAccess.maps, `${API_URL}/api/maps/farmers`),
                enabledFetch(dashboardAccess.maps && dashboardAccess.marketplace, `${API_URL}/api/maps/aggregation-centers`),
                canReviewFundingRequests
                    ? fetch(`${API_URL}/api/owner/budget`, { headers }).catch(() => null)
                    : Promise.resolve(null)
            ]);

            if (fundingRequestsResponse?.ok) {
                const moneyData = await fundingRequestsResponse.json();
                setFundingRequests((Array.isArray(moneyData?.requests) ? moneyData.requests : [])
                    .filter((request: FundingRequest) => request.status === 'pending'));
                setFundingRequestError(null);
            } else if (canReviewFundingRequests) {
                setFundingRequestError('Operations cash requests could not be loaded. Open Money to retry.');
            } else {
                setFundingRequests([]);
                setFundingRequestError(null);
            }

            // KPI Data
            if (response.ok) {
                const raw = await response.json();
                const data = (raw?.kpis || raw?.data || raw) as Partial<DashboardKPIData>;
                setKpiData({
                    farmers: Number(data.farmers ?? 0),
                    vslaSavings: Number(data.vslaSavings ?? 0),
                    compostProduced: Number(data.compostProduced ?? 0),
                    totalRevenue: Number(data.totalRevenue ?? 0),
                    trainingSessions: Number(data.trainingSessions ?? 0),
                    timestamp: typeof data.timestamp === 'string' ? data.timestamp : undefined,
                });
            } else {
                const err = await response.json().catch(() => null);
                console.warn('KPI fetch failed:', response.status, err);
                setError(err?.error || 'Failed to load KPI data');
            }

            // Charts Data
            if (chartsResponse?.ok) {
                const cData = await chartsResponse.json();
                setChartData(cData);
            } else if (!shouldLoadCharts) {
                setChartData(null);
            }

            // Events
            if (eventsResponse?.ok) {
                const eData = await eventsResponse.json();
                setEventsData(eData);
            } else if (!shouldLoadEvents) {
                setEventsData([]);
            }

            // Recent Orders
            if (ordersResponse?.ok) {
                const ordersData = await ordersResponse.json();
                const mappedOrders = (ordersData.orders || []).slice(0, 5).map((o: any) => ({
                    id: o.id.toString(),
                    orderNumber: o.order_number,
                    buyerName: o.customer_name,
                    totalValue: parseFloat(o.total_amount),
                    status: o.order_status,
                    paymentStatus: o.payment_status,
                    createdAt: o.created_at
                }));
                setRecentOrders(mappedOrders);
            } else if (!dashboardAccess.orders) {
                setRecentOrders([]);
            }

            // Warehouse Stats
            if (warehouseStatsResponse?.ok) {
                const wStats = await warehouseStatsResponse.json();
                setWarehouseStats({
                    totalFacilities: wStats.totalFacilities || 0,
                    totalCapacity: wStats.totalCapacity || 0,
                    totalStored: wStats.totalStored || 0,
                    usagePercentage: wStats.usagePercentage || 0,
                    utilizationRate: wStats.usagePercentage || 0,
                    lossRate: wStats.lossRate || 0,
                    operationalFacilities: wStats.operationalFacilities || 0,
                    avgStorageDuration: wStats.avgStorageDuration || 0,
                    maintenanceFund: wStats.maintenanceFund || 0,
                    revenue: wStats.revenue || 0,
                    losses: wStats.losses || 0
                });
            } else if (!dashboardAccess.warehouse) {
                setWarehouseStats(null);
                setWarehouses([]);
            }

            // Map Stats
            if (mapStatsResponse?.ok) {
                const mStats = await mapStatsResponse.json();
                setMapStats(mStats);
            } else if (!dashboardAccess.maps) {
                setMapStats(null);
                setFarmers([]);
                setAggregationCenters([]);
            }

            // Warehouses List
            if (warehousesResponse?.ok) {
                const wData = await warehousesResponse.json();
                setWarehouses((wData as any[]).map((w: any) => ({
                    ...w,
                    location_lat: Number(w.location_lat),
                    location_lng: Number(w.location_lng),
                    capacity_kg: toNumber(w.capacity_kg),
                    current_usage_kg: toNumber(w.current_usage_kg),
                    usage_percentage: toNumber(w.usage_percentage),
                    type: w.type || 'warehouse',
                    status: w.status || 'unknown'
                })));
            }

            // Farmers (with robust parsing)
            if (farmersResponse?.ok) {
                const fData = await farmersResponse.json();
                setFarmers((fData as any[]).map((f: any) => ({
                    ...f,
                    plot_boundary: (() => {
                        let coords = f.plot_boundary_coordinates;
                        if (typeof coords === 'string') {
                            try {
                                coords = JSON.parse(coords);
                            } catch (e) {
                                console.warn('Failed to parse plot_boundary_coordinates', e);
                                return [];
                            }
                        }
                        return Array.isArray(coords) ? coords.map((c: any) => ({
                            lat: Number(c.lat),
                            lng: Number(c.lng)
                        })) : [];
                    })()
                })));
            }

            // Aggregation Centers
            if (aggregationCentersResponse?.ok) {
                const aData = await aggregationCentersResponse.json();
                setAggregationCenters((aData as any[]).map((a: any) => ({
                    ...a,
                    location_lat: Number(a.location_lat),
                    location_lng: Number(a.location_lng)
                })));
            }

        } catch (err) {
            console.error('Dashboard fetch error:', err);
            setError('Failed to load dashboard data');
        }
    };

    const handleDismissAlert = async (id: number) => {
        try {
            await apiPatch(`/api/alerts/${id}/dismiss`, {});
            setAlerts(prev => prev.filter(alert => alert.id !== id));
        } catch (error) {
            console.error('Failed to dismiss alert:', error);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                {/* Hero Skeleton */}
                <div className="rounded-2xl bg-gradient-to-br from-brand-blue-200 to-indigo-200 p-8 animate-pulse">
                    <div className="h-4 w-48 bg-white/40 rounded mb-3" />
                    <div className="h-8 w-64 bg-white/50 rounded mb-3" />
                    <div className="h-4 w-80 bg-white/30 rounded" />
                </div>
                {/* KPI Cards Skeleton */}
                <CardSkeleton count={6} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center">
                <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center max-w-md mx-auto">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-red-100 flex items-center justify-center mb-4">
                        <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    </div>
                    <p className="text-red-800 font-bold text-lg">Something went wrong</p>
                    <p className="text-red-600 text-sm mt-1">{error}</p>
                    <button
                        onClick={fetchDashboardData}
                        className="mt-5 bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 rounded-xl font-semibold transition-all shadow-lg shadow-red-200"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-12">
            {/* ═══════ HERO HEADER ═══════ */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-brand-blue-600 via-brand-blue-700 to-indigo-800 p-6 lg:p-8 shadow-xl shadow-brand-blue-600/15">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
                <div className="absolute top-1/2 right-1/4 w-32 h-32 bg-brand-gold/10 rounded-full blur-2xl" />
                
                <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-brand-gold text-lg">✦</span>
                            <p className="text-blue-200 text-sm font-medium">{staffOpsSummary}</p>
                        </div>
                        <h1 className="text-2xl lg:text-3xl font-bold text-white tracking-tight">
                            {t('dashboard.project_overview')}
                        </h1>
                        <p className="text-blue-200 text-sm mt-1.5">
                            Last updated: {lastUpdated.toLocaleTimeString()} · Auto-refreshes every 5 min
                        </p>
                    </div>

                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-1 flex items-center border border-white/10">
                            {(['7d', '30d', 'ytd'] as const).map((period) => (
                                <button
                                    key={period}
                                    onClick={() => setTimeFilter(period)}
                                    className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                                        timeFilter === period 
                                            ? 'bg-white text-brand-blue-700 shadow-lg' 
                                            : 'text-blue-200 hover:text-white hover:bg-white/10'
                                    }`}
                                >
                                    {period.toUpperCase()}
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={() => refreshData()}
                            className="p-2.5 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10 text-white hover:bg-white/20 transition-all"
                            title="Refresh Data"
                        >
                            <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Alert Banners */}
            {alerts.length > 0 && (
                <div className="space-y-3">
                    {alerts.slice(0, 3).map(alert => (
                        <AlertBanner
                            key={alert.id}
                            alert={alert}
                            onDismiss={handleDismissAlert}
                        />
                    ))}
                </div>
            )}

            {canReviewFundingRequests && (
                <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                    <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
                        <div>
                            <h2 className="text-base font-black text-slate-900">Money requests</h2>
                            <p className="text-xs text-slate-500">Additional operations cash requests waiting for your decision.</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-black text-amber-800">
                                {fundingRequests.length} pending
                            </span>
                            <Link to="/dashboard/money" className="text-sm font-bold text-emerald-800 hover:text-emerald-900">
                                Open Money
                            </Link>
                        </div>
                    </header>
                    {fundingRequestError ? (
                        <p className="px-4 py-4 text-sm text-amber-800" role="alert">{fundingRequestError}</p>
                    ) : fundingRequests.length === 0 ? (
                        <p className="px-4 py-4 text-sm text-slate-500">No pending money requests.</p>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {fundingRequests.slice(0, 4).map((request) => (
                                <li key={request.id}>
                                    <Link to="/dashboard/money" className="flex flex-wrap items-start gap-3 px-4 py-3 hover:bg-slate-50">
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-bold capitalize text-slate-900">
                                                {(request.category || 'other').replace(/_/g, ' ')}
                                                {' · '}{formatCurrency(toNumber(request.amount))}
                                            </p>
                                            <p className="mt-1 line-clamp-2 text-sm text-slate-600">{request.reason || 'No reason provided.'}</p>
                                            <p className="mt-1 text-xs text-slate-500">
                                                {[request.requested_by, request.created_at ? new Date(request.created_at).toLocaleDateString() : null]
                                                    .filter(Boolean).join(' · ')}
                                            </p>
                                        </div>
                                        <span className="shrink-0 self-center text-xs font-bold text-emerald-800">Review</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                    <p className="border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
                        Approval authorises spending; it does not record a transfer or payment.
                    </p>
                </section>
            )}

            {/* ═══════ KPI SUMMARY ═══════ */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-4">
                {dashboardAccess.farmers && (
                    <KPICard
                        title={t('dashboard.active_farmers')}
                        value={String(kpiData?.farmers ?? 0)}
                        icon={<UserGroupIcon />}
                        color="blue"
                        to="/dashboard/farmers"
                    />
                )}
                {dashboardAccess.maps && (
                    <KPICard
                        title="Mapped Fields"
                        value={String(farmers.filter(f => Array.isArray(f.plot_boundary) && f.plot_boundary.length >= 3).length)}
                        icon={<ChartBarIcon />}
                        color="purple"
                        to="/dashboard/maps"
                    />
                )}
                {dashboardAccess.vsla && (
                    <KPICard
                        title={t('dashboard.vsla_savings')}
                        value={formatCurrencyCompact((kpiData?.vslaSavings || 0) / 1000)}
                        icon={<CurrencyDollarIcon />}
                        color="green"
                        to="/dashboard/vsla"
                    />
                )}
                {dashboardAccess.compost && (
                    <KPICard
                        title={t('dashboard.compost_produced')}
                        value={String(kpiData?.compostProduced ?? 0)}
                        icon={<GlobeAltIcon />}
                        color="amber"
                        to="/dashboard/compost"
                    />
                )}
                {dashboardAccess.orders && (
                    <KPICard
                        title={t('dashboard.revenue')}
                        value={formatCurrencyCompact((kpiData?.totalRevenue || 0) / 1000000)}
                        subtitle={t('dashboard.net_income')}
                        icon={<CurrencyDollarIcon />}
                        color="emerald"
                        to="/dashboard/orders"
                    />
                )}
                {dashboardAccess.training && (
                    <KPICard
                        title="Training Sessions"
                        value={String(kpiData?.trainingSessions ?? 0)}
                        subtitle="Attendance tracked in Training"
                        icon={<AcademicCapIcon />}
                        color="indigo"
                        to="/dashboard/training"
                    />
                )}
            </div>

            {/* 5. Quick Action Tiles */}
            <QuickActions />

            {/* ═══════ CHARTS & MAP ═══════ */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
                {/* Main Charts Area (2/3 width) */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="min-w-0">
                        <DashboardCharts data={chartData} access={dashboardAccess} />
                    </div>

                    {/* Recent Orders Summary */}
                    {dashboardAccess.orders && (
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                <ShoppingCartIcon className="w-5 h-5 text-brand-blue-600" />
                                {t('dashboard.recent_orders')}
                            </h3>
                            <span className="text-xs font-medium text-slate-500 bg-slate-50 px-2 py-1 rounded-full">
                                Last 5 orders
                            </span>
                        </div>
                        {recentOrders.length > 0 ? (
                            <div className="space-y-3">
                                {recentOrders.map((order) => (
                                    <div key={order.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-2 h-2 rounded-full ${order.status === 'delivered' ? 'bg-green-500' :
                                                order.status === 'pending' ? 'bg-yellow-500' :
                                                    order.status === 'processing' ? 'bg-blue-500' :
                                                        'bg-gray-400'
                                                }`} />
                                            <div>
                                                <p className="text-sm font-medium text-slate-800">{order.orderNumber}</p>
                                                <p className="text-xs text-slate-500">{order.buyerName}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm font-semibold text-slate-800">
                                                {formatCurrency(order.totalValue)}
                                            </p>
                                            <p className="text-xs text-slate-500 capitalize">{order.status}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-slate-500 text-center py-4">No recent orders</p>
                        )}
                    </div>
                    )}

                    {/* Warehouse Utilization Summary */}
                    {dashboardAccess.warehouse && (
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                <BuildingStorefrontIcon className="w-5 h-5 text-brand-blue-600" />
                                {t('dashboard.warehouse_utilization')}
                            </h3>
                        </div>
                        {warehouseStats ? (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <BuildingStorefrontIcon className="w-4 h-4 text-blue-600" />
                                        <span className="text-xs font-medium text-blue-700">Facilities</span>
                                    </div>
                                    <p className="text-2xl font-bold text-blue-800">{warehouseStats.totalFacilities || 0}</p>
                                    <p className="text-xs text-blue-600 mt-1">{warehouseStats.operationalFacilities || 0} operational</p>
                                </div>
                                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <CubeIcon className="w-4 h-4 text-green-600" />
                                        <span className="text-xs font-medium text-green-700">Capacity</span>
                                    </div>
                                    <p className="text-2xl font-bold text-green-800">
                                        {`${(parseFloat(String(warehouseStats.totalCapacity || 0)) / 1000).toFixed(0)}K kg`}
                                    </p>
                                    <p className="text-xs text-green-600 mt-1">Total capacity</p>
                                </div>
                                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <TruckIcon className="w-4 h-4 text-purple-600" />
                                        <span className="text-xs font-medium text-purple-700">Stored</span>
                                    </div>
                                    <p className="text-2xl font-bold text-purple-800">
                                        {`${(parseFloat(String(warehouseStats.totalStored || 0)) / 1000).toFixed(0)}K kg`}
                                    </p>
                                    <p className="text-xs text-purple-600 mt-1">Current stock</p>
                                </div>
                                <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <ChartBarIcon className="w-4 h-4 text-amber-600" />
                                        <span className="text-xs font-medium text-amber-700">Utilization</span>
                                    </div>
                                    <p className="text-2xl font-bold text-amber-800">
                                        {`${parseFloat(String(warehouseStats.usagePercentage || warehouseStats.utilizationRate || 0)).toFixed(0)}%`}
                                    </p>
                                    <p className="text-xs text-amber-600 mt-1">
                                        {`${parseFloat(String(warehouseStats.lossRate || 0)).toFixed(1)}% loss rate`}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-slate-500 text-center py-4">Loading warehouse data...</p>
                        )}
                    </div>
                    )}

                    {/* Warehouse List Table */}
                    {dashboardAccess.warehouse && (
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                <BuildingStorefrontIcon className="w-5 h-5 text-brand-blue-600" />
                                {t('nav.warehouse', 'All Warehouses')}
                            </h3>
                            <span className="text-xs font-medium text-slate-500 bg-slate-50 px-2 py-1 rounded-full">
                                {warehouses.length} facilities
                            </span>
                        </div>
                        {warehouses.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-slate-50 border-b border-slate-200">
                                            <th className="px-4 py-3 text-left font-semibold text-slate-700">Name</th>
                                            <th className="px-4 py-3 text-left font-semibold text-slate-700">Type</th>
                                            <th className="px-4 py-3 text-left font-semibold text-slate-700">Capacity</th>
                                            <th className="px-4 py-3 text-left font-semibold text-slate-700">Usage</th>
                                            <th className="px-4 py-3 text-left font-semibold text-slate-700">Utilization</th>
                                            <th className="px-4 py-3 text-left font-semibold text-slate-700">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {warehouses.map((warehouse) => (
                                            <tr key={warehouse.id} className="border-b border-slate-100 hover:bg-slate-50 transition">
                                                <td className="px-4 py-3 font-medium text-slate-800">{warehouse.name}</td>
                                                <td className="px-4 py-3 text-slate-600 capitalize">{labelize(warehouse.type, 'warehouse')}</td>
                                                <td className="px-4 py-3 text-slate-600">{warehouse.capacity_kg.toLocaleString()} kg</td>
                                                <td className="px-4 py-3 text-slate-600">{warehouse.current_usage_kg.toLocaleString()} kg</td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-16 bg-slate-200 rounded-full h-2">
                                                            <div
                                                                className="h-2 rounded-full bg-brand-blue-600"
                                                                style={{ width: `${Math.min(warehouse.usage_percentage, 100)}%` }}
                                                            ></div>
                                                        </div>
                                                        <span className="text-slate-600">{warehouse.usage_percentage.toFixed(0)}%</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${warehouse.status === 'operational' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                                        }`}>
                                                        {String(warehouse.status || 'unknown').toUpperCase()}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="text-sm text-slate-500 text-center py-4">No warehouses found</p>
                        )}
                    </div>
                    )}
                </div>

                {/* Sidebar (1/3 width) */}
                <div className="space-y-6">
                    {/* 4. Mini Map with Stats */}
                    {dashboardAccess.maps && (
                    <div>
                        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 mb-4">
                            <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
                                <GlobeAltIcon className="w-4 h-4 text-brand-blue-600" />
                                {t('dashboard.geographic_overview')}
                            </h3>
                            {mapStats && (
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div className="bg-slate-50 rounded-lg p-2">
                                        <p className="text-slate-500">Mapped fields</p>
                                        <p className="font-bold text-slate-800">
                                            {farmers.filter(f => Array.isArray(f.plot_boundary) && f.plot_boundary.length >= 3).length}
                                        </p>
                                    </div>
                                    <div className="bg-slate-50 rounded-lg p-2">
                                        <p className="text-slate-500">{t('dashboard.geo_farmers')}</p>
                                        <p className="font-bold text-slate-800">{mapStats.total_farmers || 0}</p>
                                    </div>
                                    <div className="bg-slate-50 rounded-lg p-2">
                                        <p className="text-slate-500">{t('dashboard.geo_warehouses')}</p>
                                        <p className="font-bold text-slate-800">{mapStats.total_warehouses || 0}</p>
                                    </div>
                                    <div className="bg-slate-50 rounded-lg p-2">
                                        <p className="text-slate-500">{t('dashboard.geo_aggregation')}</p>
                                        <p className="font-bold text-slate-800">{mapStats.total_aggregation_centers || 0}</p>
                                    </div>
                                    <div className="bg-slate-50 rounded-lg p-2">
                                        <p className="text-slate-500">{t('dashboard.geo_area')}</p>
                                        <p className="font-bold text-slate-800">{(mapStats.total_area_hectares || 0).toFixed(1)} ha</p>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="h-64">
                            <MiniMap cohorts={[]} warehouses={warehouses} farmers={farmers} />
                        </div>
                    </div>
                    )}

                    {/* 8. Upcoming Events */}
                    <UpcomingEvents events={eventsData} access={dashboardAccess} />

                    {/* ═══════ ACTIVITY FEED ═══════ */}
                    {dashboardAccess.activities && (
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-slate-800">{t('dashboard.recent_activity')}</h3>
                            <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full flex items-center gap-1">
                                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span> {t('common.live', 'Live')}
                            </span>
                        </div>
                        <ActivityFeed />
                    </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
